<template>
  <!-- Two trend lines on ONE shared scale (e.g. traffic in vs out). KvmSparkline
       wraps v-sparkline, which normalises each series to its own min/max — two of
       them stacked would draw a 1 KB/s trickle as tall as a 100 MB/s burst. So this
       draws plain SVG paths against a common peak instead. -->
  <div class="vds-dspark" :style="{ height: `${height}px` }" role="presentation" aria-hidden="true">
    <svg :viewBox="`0 0 ${W} ${height}`" preserveAspectRatio="none">
      <path :d="areaPath(pointsA)" :fill="colorA" class="vds-dspark__fill" />
      <path :d="linePath(pointsA)" :stroke="colorA" :stroke-width="lineWidth" fill="none"
            stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
      <path :d="linePath(pointsB)" :stroke="colorB" :stroke-width="lineWidth" fill="none"
            stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
    </svg>
  </div>
</template>

<script>
// Average dense ranges down to at most `max` buckets so the line reads as a trend.
const downsample = (values, max) => {
  const v = (values || []).map(n => (n == null ? 0 : n))
  if (v.length <= max) return v
  const bucket = Math.ceil(v.length / max)
  const out = []
  for (let i = 0; i < v.length; i += bucket) {
    const slice = v.slice(i, i + bucket)
    out.push(slice.reduce((a, b) => a + b, 0) / slice.length)
  }
  return out
}

export default {
  name: 'KvmDualSparkline',
  props: {
    a: { type: Array, default: () => [] },
    b: { type: Array, default: () => [] },
    colorA: { type: String, default: '#0ca750' },
    colorB: { type: String, default: '#1e88e5' },
    height: { type: Number, default: 44 },
    lineWidth: { type: Number, default: 2 },
    padding: { type: Number, default: 4 },
    maxPoints: { type: Number, default: 48 },
  },
  data: () => ({ W: 240 }),
  computed: {
    pointsA () { return downsample(this.a, this.maxPoints) },
    pointsB () { return downsample(this.b, this.maxPoints) },
    // Common peak across both series; floor of 1 keeps an all-zero range flat.
    peak () { return Math.max(1, ...this.pointsA, ...this.pointsB) },
  },
  methods: {
    coords (points) {
      const n = points.length
      const usable = this.height - this.padding * 2
      return points.map((v, i) => [
        n > 1 ? (i / (n - 1)) * this.W : this.W / 2,
        this.padding + usable - (v / this.peak) * usable,
      ])
    },
    linePath (points) {
      if (!points.length) return ''
      return this.coords(points).map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
    },
    areaPath (points) {
      if (!points.length) return ''
      const bottom = this.height
      const pts = this.coords(points)
      return `${this.linePath(points)} L${pts[pts.length - 1][0].toFixed(1)},${bottom} L${pts[0][0].toFixed(1)},${bottom} Z`
    },
  },
}
</script>

<style scoped lang="scss">
.vds-dspark {
  position: relative;
  width: 100%;

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}
/* A soft wash under the first series only, so the two lines stay legible. */
.vds-dspark__fill {
  opacity: 0.14;
}
</style>
